import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createInvoice,
  createSubscription,
  patchBillingProfile,
  patchInvoice,
  recordPayment,
  recordTicoFactura,
  useBillingOverview,
  useBusinessProfile,
  useInvoices,
  useSubscriptions,
  type InvoiceLineItem,
  type InvoiceRow,
  type SubscriptionRow,
} from "../lib/tenant-api";
import { formatMoney } from "../lib/format";
import {
  EmptyState,
  ErrorBanner,
  LoadingRow,
} from "../components/EmptyState";

const TICO_FACTURA_URL = "https://tribu-cr.hacienda.go.cr/";

export default function Billing() {
  const overview = useBillingOverview();
  const profile = useBusinessProfile();
  const invoices = useInvoices();
  const subs = useSubscriptions();
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["tenant", "billing"] });
  };

  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [createSubOpen, setCreateSubOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [paymentFor, setPaymentFor] = useState<InvoiceRow | null>(null);
  const [ticoFor, setTicoFor] = useState<InvoiceRow | null>(null);
  const [paidBanner, setPaidBanner] = useState<string | null>(null);

  const profileIncomplete =
    !!profile.data &&
    (!profile.data.legal_name || !profile.data.tax_id || !profile.data.billing_email);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Billing</h1>
          <p className="mt-1 text-sm text-slate-500">
            Subscriptions, invoices, and payments. The legal factura electrónica
            is emitted separately in Tico Factura — record the consecutivo here
            for traceability.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCreateSubOpen(true)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            + Subscription
          </button>
          <button
            onClick={() => setCreateInvoiceOpen(true)}
            className="px-3 py-1.5 text-sm rounded-lg bg-brand-600 text-white hover:bg-brand-700"
          >
            + Invoice
          </button>
        </div>
      </header>

      {paidBanner && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex items-start gap-3">
          <span className="text-xl leading-none">⚠</span>
          <div className="flex-1">
            <div className="font-medium">{paidBanner}</div>
            <div className="mt-1 text-amber-800">
              Recordá emitir la factura electrónica legal en Tico Factura y
              pegar el consecutivo aquí cuando lo tengas.
            </div>
            <a
              href={TICO_FACTURA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-amber-900 underline font-medium"
            >
              Abrir Tico Factura →
            </a>
          </div>
          <button
            onClick={() => setPaidBanner(null)}
            className="text-amber-700 hover:text-amber-900 text-xl leading-none"
          >
            ×
          </button>
        </div>
      )}

      {profileIncomplete && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900 flex items-start gap-3">
          <span className="text-xl leading-none">ℹ</span>
          <div className="flex-1">
            <div className="font-medium">
              Completá tu identidad fiscal antes de emitir facturas
            </div>
            <div className="mt-1">
              Razón social, cédula jurídica y email de cobro se snapshotean en
              cada factura — sin estos campos el PDF y la factura electrónica
              quedan incompletos.
            </div>
          </div>
          <button
            onClick={() => setEditProfileOpen(true)}
            className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Editar perfil
          </button>
        </div>
      )}

      {/* Overview cards */}
      {overview.data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="Outstanding"
            value={
              overview.data.outstanding_cents > 0
                ? formatMoney(overview.data.outstanding_cents, "CRC")
                : "—"
            }
          />
          <Stat
            label="Pending"
            value={String(overview.data.counts.pending_invoices)}
          />
          <Stat
            label="Overdue"
            value={String(overview.data.counts.overdue_invoices)}
            tone={overview.data.counts.overdue_invoices > 0 ? "warn" : "neutral"}
          />
          <Stat
            label="Active subs"
            value={String(overview.data.counts.active_subscriptions)}
          />
        </div>
      )}

      {/* Invoices */}
      <section>
        <h2 className="text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
          Invoices
        </h2>
        {invoices.isLoading && <LoadingRow />}
        {invoices.error && (
          <ErrorBanner message={(invoices.error as Error).message} />
        )}
        {invoices.data && invoices.data.length === 0 && (
          <EmptyState
            icon="🧾"
            title="No invoices yet"
            hint='Click "+ Invoice" to issue your first one.'
          />
        )}
        {invoices.data && invoices.data.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left font-medium px-4 py-2">#</th>
                  <th className="text-left font-medium px-4 py-2">Issued</th>
                  <th className="text-left font-medium px-4 py-2">Due</th>
                  <th className="text-right font-medium px-4 py-2">Total</th>
                  <th className="text-left font-medium px-4 py-2">Status</th>
                  <th className="text-left font-medium px-4 py-2">Tico Factura</th>
                  <th className="text-right font-medium px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.data.map((inv) => (
                  <tr key={inv.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-mono text-slate-700">
                      {inv.invoice_number}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {inv.issue_date}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {inv.due_date}
                      {inv.is_overdue && (
                        <span className="ml-2 text-xs text-red-600 font-medium">
                          overdue
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900 font-medium">
                      {formatMoney(inv.total_cents, inv.currency)}
                      {inv.total_paid_cents > 0 && inv.balance_cents > 0 && (
                        <div className="text-xs text-slate-500">
                          {formatMoney(inv.total_paid_cents, inv.currency)} paid
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <InvoiceStatusPill status={inv.status} />
                    </td>
                    <td className="px-4 py-3">
                      {inv.tico_factura_consecutivo ? (
                        <span
                          className="text-xs font-mono text-slate-600 truncate"
                          title={inv.tico_factura_consecutivo}
                        >
                          {inv.tico_factura_consecutivo.slice(-8)}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <a
                        href={`/api/tenant/billing/invoices/${inv.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-slate-600 hover:text-slate-900 px-2"
                        title="Download PDF"
                      >
                        PDF
                      </a>
                      {inv.status !== "paid" &&
                        inv.status !== "void" &&
                        inv.status !== "cancelled" && (
                          <button
                            onClick={() => setPaymentFor(inv)}
                            className="text-xs text-emerald-700 hover:text-emerald-900 px-2"
                          >
                            Mark paid
                          </button>
                        )}
                      {!inv.tico_factura_consecutivo && (
                        <button
                          onClick={() => setTicoFor(inv)}
                          className="text-xs text-blue-700 hover:text-blue-900 px-2"
                        >
                          Tico Factura
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Subscriptions */}
      <section>
        <h2 className="text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
          Subscriptions
        </h2>
        {subs.isLoading && <LoadingRow />}
        {subs.data && subs.data.length === 0 && (
          <EmptyState
            icon="🔁"
            title="No subscriptions"
            hint="Subscriptions track recurring billing schedules. Optional — you can issue one-off invoices without them."
          />
        )}
        {subs.data && subs.data.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Name</th>
                  <th className="text-right font-medium px-4 py-2">Amount</th>
                  <th className="text-left font-medium px-4 py-2">Cycle</th>
                  <th className="text-left font-medium px-4 py-2">Next invoice</th>
                  <th className="text-left font-medium px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {subs.data.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-900">{s.name}</td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      {formatMoney(s.amount_cents, s.currency)}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{s.billing_cycle}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {s.next_invoice_date ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <SubStatusPill status={s.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Modals */}
      {createInvoiceOpen && (
        <CreateInvoiceModal
          subscriptions={subs.data ?? []}
          onClose={() => setCreateInvoiceOpen(false)}
          onCreated={() => {
            setCreateInvoiceOpen(false);
            invalidate();
          }}
        />
      )}
      {createSubOpen && (
        <CreateSubscriptionModal
          onClose={() => setCreateSubOpen(false)}
          onCreated={() => {
            setCreateSubOpen(false);
            invalidate();
          }}
        />
      )}
      {paymentFor && (
        <RecordPaymentModal
          invoice={paymentFor}
          onClose={() => setPaymentFor(null)}
          onPaid={(invoiceStatus) => {
            const inv = paymentFor;
            setPaymentFor(null);
            invalidate();
            if (invoiceStatus === "paid") {
              setPaidBanner(
                `Pago registrado en factura #${inv.invoice_number}.`,
              );
            }
          }}
        />
      )}
      {ticoFor && (
        <TicoFacturaModal
          invoice={ticoFor}
          onClose={() => setTicoFor(null)}
          onSaved={() => {
            setTicoFor(null);
            invalidate();
          }}
        />
      )}
      {editProfileOpen && profile.data && (
        <BillingProfileModal
          current={{
            legal_name: profile.data.legal_name,
            billing_email: profile.data.billing_email,
            tax_id: profile.data.tax_id,
            tax_id_type: profile.data.tax_id_type,
            billing_address: profile.data.billing_address,
          }}
          onClose={() => setEditProfileOpen(false)}
          onSaved={() => {
            setEditProfileOpen(false);
            qc.invalidateQueries({ queryKey: ["tenant", "business-profile"] });
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "warn";
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        tone === "warn"
          ? "border-amber-200 bg-amber-50"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="text-xs uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-bold ${
          tone === "warn" ? "text-amber-700" : "text-slate-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function InvoiceStatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: "bg-slate-200 text-slate-600",
    pending: "bg-amber-100 text-amber-700",
    overdue: "bg-red-100 text-red-700",
    paid: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-slate-200 text-slate-500 line-through",
    void: "bg-slate-200 text-slate-500 line-through",
  };
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        map[status] ?? "bg-slate-100 text-slate-600"
      }`}
    >
      {status}
    </span>
  );
}

function SubStatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: "bg-emerald-100 text-emerald-700",
    paused: "bg-amber-100 text-amber-700",
    cancelled: "bg-slate-200 text-slate-500 line-through",
  };
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        map[status] ?? "bg-slate-100 text-slate-600"
      }`}
    >
      {status}
    </span>
  );
}

function ModalShell({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2 rounded-b-2xl">
          {footer}
        </div>
      </div>
    </div>
  );
}

function FormField({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block mb-3">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
      {hint && <span className="text-xs text-slate-500 mt-1 block">{hint}</span>}
    </label>
  );
}

const inputCls =
  "mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400";

// -------- Create Invoice -------------------------------------------------
function CreateInvoiceModal({
  subscriptions,
  onClose,
  onCreated,
}: {
  subscriptions: SubscriptionRow[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const today = todayLocalDate();
  const dueDefault = isoPlusDays(today, 14);
  const [subscriptionId, setSubscriptionId] = useState<string>("");
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState(dueDefault);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [currency, setCurrency] = useState<"CRC" | "USD">("CRC");
  const [taxRateBps, setTaxRateBps] = useState<number>(1300);
  const [items, setItems] = useState<InvoiceLineItem[]>([
    { description: "", quantity: 1, unit_price_cents: 0, total_cents: 0 },
  ]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subtotal = useMemo(
    () => items.reduce((acc, it) => acc + it.total_cents, 0),
    [items],
  );
  const tax = Math.round((subtotal * taxRateBps) / 10_000);
  const total = subtotal + tax;

  function setItem(idx: number, patch: Partial<InvoiceLineItem>) {
    setItems((prev) => {
      const next = [...prev];
      const merged = { ...next[idx], ...patch };
      merged.total_cents = Math.round(
        merged.quantity * merged.unit_price_cents,
      );
      next[idx] = merged;
      return next;
    });
  }

  async function submit(status: "draft" | "pending") {
    setError(null);
    setSubmitting(true);
    try {
      await createInvoice({
        subscription_id: subscriptionId || null,
        issue_date: issueDate,
        due_date: dueDate,
        period_start: periodStart || null,
        period_end: periodEnd || null,
        line_items: items.filter((i) => i.description.trim() && i.total_cents > 0),
        currency,
        tax_rate_bps: taxRateBps,
        notes: notes || null,
        status,
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title="New invoice"
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            disabled={submitting}
            onClick={() => submit("draft")}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Save as draft
          </button>
          <button
            disabled={submitting || total <= 0}
            onClick={() => submit("pending")}
            className="px-3 py-1.5 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      {subscriptions.length > 0 && (
        <FormField label="Linked subscription (optional)">
          <select
            className={inputCls}
            value={subscriptionId}
            onChange={(e) => setSubscriptionId(e.target.value)}
          >
            <option value="">— none —</option>
            {subscriptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({formatMoney(s.amount_cents, s.currency)} /{" "}
                {s.billing_cycle})
              </option>
            ))}
          </select>
        </FormField>
      )}
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Issue date">
          <input
            type="date"
            className={inputCls}
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
          />
        </FormField>
        <FormField label="Due date">
          <input
            type="date"
            className={inputCls}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </FormField>
        <FormField label="Period start (optional)">
          <input
            type="date"
            className={inputCls}
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </FormField>
        <FormField label="Period end (optional)">
          <input
            type="date"
            className={inputCls}
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </FormField>
        <FormField label="Currency">
          <select
            className={inputCls}
            value={currency}
            onChange={(e) => setCurrency(e.target.value as "CRC" | "USD")}
          >
            <option value="CRC">CRC</option>
            <option value="USD">USD</option>
          </select>
        </FormField>
        <FormField label="Tax rate" hint="Basis points; 1300 = 13% IVA, 0 = exento">
          <input
            type="number"
            min={0}
            max={10000}
            className={inputCls}
            value={taxRateBps}
            onChange={(e) => setTaxRateBps(Number(e.target.value))}
          />
        </FormField>
      </div>

      <div className="mt-2 mb-1 text-xs font-medium text-slate-700">
        Line items
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="flex gap-2 items-start">
            <input
              type="text"
              placeholder="Description"
              className={`${inputCls} flex-1`}
              value={it.description}
              onChange={(e) => setItem(idx, { description: e.target.value })}
            />
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder="Qty"
              className={`${inputCls} w-20`}
              value={it.quantity}
              onChange={(e) =>
                setItem(idx, { quantity: Number(e.target.value) })
              }
            />
            <input
              type="number"
              min={0}
              placeholder="Unit (¢)"
              className={`${inputCls} w-28`}
              value={it.unit_price_cents}
              onChange={(e) =>
                setItem(idx, { unit_price_cents: Number(e.target.value) })
              }
            />
            <button
              onClick={() =>
                setItems((prev) => prev.filter((_, i) => i !== idx))
              }
              className="text-slate-400 hover:text-red-600 text-lg px-1"
              disabled={items.length === 1}
              title="Remove line"
            >
              ×
            </button>
          </div>
        ))}
        <button
          onClick={() =>
            setItems((prev) => [
              ...prev,
              { description: "", quantity: 1, unit_price_cents: 0, total_cents: 0 },
            ])
          }
          className="text-sm text-brand-700 hover:text-brand-900"
        >
          + Add line
        </button>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-200 text-sm space-y-1">
        <div className="flex justify-between text-slate-600">
          <span>Subtotal</span>
          <span>{formatMoney(subtotal, currency)}</span>
        </div>
        {tax > 0 && (
          <div className="flex justify-between text-slate-600">
            <span>Tax</span>
            <span>{formatMoney(tax, currency)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-slate-900">
          <span>Total</span>
          <span>{formatMoney(total, currency)}</span>
        </div>
      </div>

      <FormField label="Notes (optional)">
        <textarea
          className={`${inputCls} min-h-[60px]`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormField>
    </ModalShell>
  );
}

// -------- Create Subscription -------------------------------------------
function CreateSubscriptionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const today = todayLocalDate();
  const [name, setName] = useState("");
  const [amountCents, setAmountCents] = useState(0);
  const [currency, setCurrency] = useState<"CRC" | "USD">("CRC");
  const [billingCycle, setBillingCycle] = useState<
    "monthly" | "quarterly" | "annual" | "one_time"
  >("monthly");
  const [startDate, setStartDate] = useState(today);
  const [nextInvoiceDate, setNextInvoiceDate] = useState(today);
  const [taxRateBps, setTaxRateBps] = useState(1300);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      await createSubscription({
        name,
        amount_cents: amountCents,
        currency,
        billing_cycle: billingCycle,
        start_date: startDate,
        next_invoice_date: nextInvoiceDate || null,
        tax_rate_bps: taxRateBps,
        notes: notes || null,
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title="New subscription"
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            disabled={submitting || !name || amountCents <= 0}
            onClick={submit}
            className="px-3 py-1.5 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <FormField label="Name">
        <input
          type="text"
          className={inputCls}
          placeholder="e.g. Receptionist - Plan Mensual"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Amount (cents)">
          <input
            type="number"
            min={0}
            className={inputCls}
            value={amountCents}
            onChange={(e) => setAmountCents(Number(e.target.value))}
          />
        </FormField>
        <FormField label="Currency">
          <select
            className={inputCls}
            value={currency}
            onChange={(e) => setCurrency(e.target.value as "CRC" | "USD")}
          >
            <option value="CRC">CRC</option>
            <option value="USD">USD</option>
          </select>
        </FormField>
        <FormField label="Cycle">
          <select
            className={inputCls}
            value={billingCycle}
            onChange={(e) =>
              setBillingCycle(e.target.value as typeof billingCycle)
            }
          >
            <option value="monthly">monthly</option>
            <option value="quarterly">quarterly</option>
            <option value="annual">annual</option>
            <option value="one_time">one-time</option>
          </select>
        </FormField>
        <FormField label="Tax rate (bps)">
          <input
            type="number"
            min={0}
            max={10000}
            className={inputCls}
            value={taxRateBps}
            onChange={(e) => setTaxRateBps(Number(e.target.value))}
          />
        </FormField>
        <FormField label="Start date">
          <input
            type="date"
            className={inputCls}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </FormField>
        <FormField label="Next invoice date">
          <input
            type="date"
            className={inputCls}
            value={nextInvoiceDate}
            onChange={(e) => setNextInvoiceDate(e.target.value)}
          />
        </FormField>
      </div>
      <FormField label="Notes (optional)">
        <textarea
          className={`${inputCls} min-h-[60px]`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormField>
    </ModalShell>
  );
}

// -------- Record Payment -------------------------------------------------
function RecordPaymentModal({
  invoice,
  onClose,
  onPaid,
}: {
  invoice: InvoiceRow;
  onClose: () => void;
  onPaid: (newStatus: string) => void;
}) {
  const balance = invoice.balance_cents;
  const [amountCents, setAmountCents] = useState(balance);
  const [paymentMethod, setPaymentMethod] = useState<
    "sinpe_movil" | "wire" | "cash" | "card" | "other"
  >("sinpe_movil");
  const [paymentDate, setPaymentDate] = useState(todayLocalDate());
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const res = await recordPayment(invoice.id, {
        amount_cents: amountCents,
        currency: invoice.currency as "CRC" | "USD",
        payment_method: paymentMethod,
        payment_date: paymentDate,
        reference: reference || null,
        notes: notes || null,
      });
      onPaid(res.invoice_status);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title={`Record payment — invoice #${invoice.invoice_number}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            disabled={submitting || amountCents <= 0}
            onClick={submit}
            className="px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? "Recording…" : "Record payment"}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="bg-slate-50 rounded-lg p-3 text-sm mb-3">
        <div className="flex justify-between">
          <span className="text-slate-600">Total</span>
          <span className="font-medium text-slate-900">
            {formatMoney(invoice.total_cents, invoice.currency)}
          </span>
        </div>
        {invoice.total_paid_cents > 0 && (
          <div className="flex justify-between mt-1">
            <span className="text-slate-600">Paid so far</span>
            <span className="text-slate-900">
              {formatMoney(invoice.total_paid_cents, invoice.currency)}
            </span>
          </div>
        )}
        <div className="flex justify-between mt-1 pt-1 border-t border-slate-200">
          <span className="text-slate-600">Balance</span>
          <span className="font-bold text-slate-900">
            {formatMoney(balance, invoice.currency)}
          </span>
        </div>
      </div>

      <FormField
        label={`Amount in ${invoice.currency} (cents)`}
        hint="Partial payments allowed; status flips to paid when sum ≥ total."
      >
        <input
          type="number"
          min={1}
          className={inputCls}
          value={amountCents}
          onChange={(e) => setAmountCents(Number(e.target.value))}
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Method">
          <select
            className={inputCls}
            value={paymentMethod}
            onChange={(e) =>
              setPaymentMethod(e.target.value as typeof paymentMethod)
            }
          >
            <option value="sinpe_movil">SINPE Móvil</option>
            <option value="wire">Wire transfer</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </select>
        </FormField>
        <FormField label="Date">
          <input
            type="date"
            className={inputCls}
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
        </FormField>
      </div>
      <FormField label="Reference (optional)" hint="SINPE confirmation, wire ref…">
        <input
          type="text"
          className={inputCls}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </FormField>
      <FormField label="Notes (optional)">
        <textarea
          className={`${inputCls} min-h-[60px]`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormField>
    </ModalShell>
  );
}

// -------- Tico Factura ---------------------------------------------------
function TicoFacturaModal({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: InvoiceRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [consecutivo, setConsecutivo] = useState(
    invoice.tico_factura_consecutivo ?? "",
  );
  const [clave, setClave] = useState(invoice.tico_factura_clave ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      await recordTicoFactura(invoice.id, {
        consecutivo,
        clave: clave || null,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title={`Tico Factura — invoice #${invoice.invoice_number}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            disabled={submitting || !consecutivo}
            onClick={submit}
            className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <p className="text-sm text-slate-600 mb-3">
        Después de emitir la factura electrónica en{" "}
        <a
          href={TICO_FACTURA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 underline"
        >
          Tico Factura
        </a>
        , pegá acá el consecutivo (y la clave si la tenés) para dejar el link
        de auditoría con esta factura comercial.
      </p>
      <FormField label="Consecutivo" hint="20 dígitos del comprobante en Hacienda">
        <input
          type="text"
          className={`${inputCls} font-mono`}
          placeholder="00100001010000000001"
          value={consecutivo}
          onChange={(e) => setConsecutivo(e.target.value)}
        />
      </FormField>
      <FormField label="Clave (opcional)" hint="50 dígitos de la clave numérica">
        <input
          type="text"
          className={`${inputCls} font-mono`}
          value={clave}
          onChange={(e) => setClave(e.target.value)}
        />
      </FormField>
    </ModalShell>
  );
}

// -------- Billing profile ------------------------------------------------
function BillingProfileModal({
  current,
  onClose,
  onSaved,
}: {
  current: {
    legal_name: string | null;
    billing_email: string | null;
    tax_id: string | null;
    tax_id_type: string | null;
    billing_address: string | null;
  };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [legalName, setLegalName] = useState(current.legal_name ?? "");
  const [billingEmail, setBillingEmail] = useState(current.billing_email ?? "");
  const [taxId, setTaxId] = useState(current.tax_id ?? "");
  const [taxIdType, setTaxIdType] = useState(
    current.tax_id_type ?? "cedula_juridica",
  );
  const [billingAddress, setBillingAddress] = useState(
    current.billing_address ?? "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      await patchBillingProfile({
        legal_name: legalName || null,
        billing_email: billingEmail || null,
        tax_id: taxId || null,
        tax_id_type: (taxIdType as BillingProfileModalTaxIdType) || null,
        billing_address: billingAddress || null,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title="Identidad fiscal del cliente"
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            disabled={submitting}
            onClick={submit}
            className="px-3 py-1.5 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? "Guardando…" : "Guardar"}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <FormField label="Razón social">
        <input
          type="text"
          className={inputCls}
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
        />
      </FormField>
      <FormField label="Email de cobro">
        <input
          type="email"
          className={inputCls}
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Tipo de identificación">
          <select
            className={inputCls}
            value={taxIdType}
            onChange={(e) => setTaxIdType(e.target.value)}
          >
            <option value="cedula_juridica">Cédula Jurídica</option>
            <option value="cedula_fisica">Cédula Física</option>
            <option value="dimex">DIMEX</option>
            <option value="nite">NITE</option>
            <option value="pasaporte">Pasaporte</option>
          </select>
        </FormField>
        <FormField label="Identificación">
          <input
            type="text"
            className={inputCls}
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
          />
        </FormField>
      </div>
      <FormField label="Dirección (opcional)">
        <textarea
          className={`${inputCls} min-h-[60px]`}
          value={billingAddress}
          onChange={(e) => setBillingAddress(e.target.value)}
        />
      </FormField>
    </ModalShell>
  );
}

type BillingProfileModalTaxIdType =
  | "cedula_juridica"
  | "cedula_fisica"
  | "dimex"
  | "nite"
  | "pasaporte";

// ============================================================
// Helpers
// ============================================================

function todayLocalDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isoPlusDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

// patchInvoice imported but exposed for future "void" / "cancel" buttons —
// referenced once below to avoid unused-import warning.
void patchInvoice;
